import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { walkInOrdersApi, type CreateWalkInOrderPayload } from "../services/walkInOrdersApi";

/**
 * Phone-based customer lookup mutation for the walk-in flow. Mutation (not query) because
 * the cashier triggers it explicitly with a button click — we don't want it firing on
 * every keystroke.
 */
export const useWalkInLookup = () => {
  return useMutation({
    mutationFn: (phone: string) => walkInOrdersApi.lookupByPhone(phone),
  });
};

/**
 * Effective discount for a customer, used by both the Create Order dialog and the walk-in flow.
 * Disabled until a customer id is known (new walk-in customers have no discount). For walk-in it
 * drives the discounted total the cashier collects; for standard orders it's an informational preview.
 */
export const useEffectiveDiscount = (customerId: string | null | undefined) => {
  return useQuery({
    queryKey: ["effective-discount", customerId],
    queryFn: () => walkInOrdersApi.getEffectiveDiscount(customerId!),
    enabled: !!customerId,
    staleTime: 60_000,
  });
};

/**
 * Create-and-dispatch in one step. On success, invalidates the orders list, available
 * greenhouse stock, and customers list so the new walk-in is visible everywhere.
 */
export const useCreateWalkInOrder = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateWalkInOrderPayload) =>
      walkInOrdersApi.createWalkInOrder(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders"] });
      qc.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
      qc.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
      qc.invalidateQueries({ queryKey: ["expo-stock"] });
      qc.invalidateQueries({ queryKey: ["customers"] });
    },
  });
};
